import type { ComponentType, PropsWithChildren, ReactNode } from 'react';
import * as React from 'react';
import { createContext, useContext } from 'react';

import type { IExtensionRegistration } from './IExtensionRegistration';

import { CurrentIdentity } from './CurrentIdentity';
import { IRoute } from './types';
import { win } from './utils';

/** The type for a Loadable Component - the distinction is handled by the ComponentLoader */
export type LoadableComponent = ComponentType | string;

export type FeatureDefinitions = {
	name: string;
	enableByDefault: boolean;
	enabled: boolean;
};

interface IExtensionContext {
	// getComponentExtensions(name: string): ComponentType[];
	// getComponentExtension(name: string): ComponentType;

	getExtensions<T>(name: string): T[];

	subscribeUpdates: (topic: string, receiver: (event: CustomEvent<unknown>) => void) => () => void;
	subscribeUpdatesArray:(topics: string[], receiver: (event: CustomEvent<unknown>) => void) => () => void;

	requestFeature: (name: string, enable: boolean) => void;
}

export const extensionTopic = (name: string) => `extension:${name}`;
export const componentExtensionTopic = <TName extends string = string>(name: TName) => `component_extension:${name}`;

const featureToggles = (typeof localStorage != 'undefined' ? JSON.parse(localStorage.getItem('FEATURES') ?? '{}') : {}) as Record<string, boolean>;

function extension<T>(name: string, ext: T) {
	if (!extensionContextStore.extensions[name]) extensionContextStore.extensions[name] = [];

	extensionContextStore.extensions[name].push(ext);

	sendUpdate(extensionTopic(name));
}

function masterOrderedComponent(component: ComponentType<PropsWithChildren>, requestedOrder: number) {
	// prevent double registering the same component
	if (extensionContextStore.master.some(a => a.comp == component)) return;

	extensionContextStore.master.push({ order: requestedOrder, comp: component });

	// this can't happen after initPlugin
}
function masterComponent(component: ComponentType<PropsWithChildren>, prepend?: boolean) {
	masterOrderedComponent(component, prepend ? 0 : 100000);
}

export const api: Omit<IExtensionRegistration, 'config'> = {
	route: (slug: string, route: IRoute) => {
		extensionContextStore.routeMatches.push({
			slug,
			slugMatch: null,
			route
		});
	},
	extension,

	asyncInit: (initFunction: () => Promise<void>) => {
		extension('async-init', initFunction());
	},

	/** @deprecated We should do non-react async extensions */
	asyncHook: (hook: () => void) => {
		extension(ASYNC_EXTENSION, (reg: boolean) => {
			if (reg) return;

			hook();
		});
	},
	componentExtension: <T, TName extends string = string>(name: TName, component: ComponentType<T>, requireFlags?: Array<string>) => {
		// define a regular extension with the flags
		extension(`component-${name}`, {
			component,
			requireFlags
		});

		// if (!extensionContextStore.componentExtensions[name]) extensionContextStore.componentExtensions[name] = [];

		// extensionContextStore.componentExtensions[name].push(component);

		sendUpdate(componentExtensionTopic<TName>(name));
	},
	masterOrderedComponent,
	masterComponent,
	feature: (name: string, enableByDefault: boolean): boolean => {
		const enabled = featureToggles[name] ?? enableByDefault;

		extensionContextStore.features.push({ name, enableByDefault, enabled });

		return enabled;
	},
	requestFeature: (name: string, enable: boolean) => {
		featureToggles[name] = enable;
		window.localStorage['FEATURES'] = JSON.stringify(featureToggles);
	},
	getAccessToken: () => CurrentIdentity && CurrentIdentity.accessToken
};

export const extensionContextValue = {
	// getComponentExtension,
	// getComponentExtensions,
	getExtensions,
	subscribeUpdates,
	subscribeUpdatesArray,
	feature: api.feature,
	requestFeature: api.requestFeature
};

export function sendUpdate(topic: string, detail?: unknown) {
	const event = new CustomEvent(`pubsub:${topic}`, {
		detail
	});
	document.dispatchEvent(event);
	// PubSub.publish(topic, true);
}

const ExtensionContext = createContext<IExtensionContext>(extensionContextValue);

export function useExtensionContext() {
	const context = useContext(ExtensionContext);
	if (!context) {
		throw new Error('Extension Context is not available.');
	}

	return context;
}

const DefaultMaster: React.FC<PropsWithChildren> = ({ children }) => <>{children}</>;

/** Obtains the master component registrations.
 *
 * This is deliberately not titled using a use prefix as it isn't using any hooks internally */
export function getMasterComponents() {
	const orderedComponents = extensionContextStore.master.sort((a, b) => a.order - b.order).map(a => a.comp);

	// obtains the master page component or defaults to React.Fragment as a noop
	return orderedComponents ?? [DefaultMaster];
}

interface IExtensionProviderProps {
	children: ReactNode;
}

interface IExtensionContextStore {
	master: { order: number; comp: ComponentType<PropsWithChildren> }[];

	routeMatches: {
		slug: string;
		slugMatch: RegExp | null; // if the path contains a : we can use pathToRegexp
		route: IRoute;
	}[];

	/** Explicit extension points */
	// componentExtensions: { [name: string]: ComponentType<any>[] };

	extensions: { [name: string]: unknown[] };

	features: FeatureDefinitions[];
}

export const extensionContextStore: IExtensionContextStore = {
	master: [],
	routeMatches: [],
	// componentExtensions: {},
	extensions: {},
	features: []
};

/** @obsolete Just use React.PropsWithChildren or React.FC  */
export type IChildrenProps<T = {}> = React.PropsWithChildren<T>;

export const extensionRegistration = (config: any): IExtensionRegistration => ({
	config,
	...api
});

/** For non-react use you may get "data" extensions from the store. Note if the extension requires react, you should call this via the
 * hook version otherwise things might not work as expected. */
export function getExtensions<T, TName extends string = string>(name: TName): T[] {
	return (extensionContextStore.extensions[name] as T[]) ?? [];
}
export function getExtension<T, TName extends string = string>(name: TName) {
	return getExtensions<T>(name)[0];
}

// function getComponentExtension(name: string) {
// 	return extensionContextStore.componentExtensions[name]?.[0] || null;
// }

function subscribeUpdates(topic: string, receiver: (event: CustomEvent<unknown>) => void) {
	document.addEventListener(`pubsub:${topic}`, receiver as (e: Event) => void);

	// const res = PubSub.subscribe(topic, receiver);

	return () => document.removeEventListener(`pubsub:${topic}`, receiver as (e: Event) => void);
}

function subscribeUpdatesArray(topics: string[], receiver: (event: CustomEvent<unknown>) => void) {
	for (const topic of topics) {
		if (!topic) continue;

		document.addEventListener(`pubsub:${topic}`, receiver as (e: Event) => void);
	}

	return () => {
		for (const topic of topics) {
			if (!topic) continue;
			document.removeEventListener(`pubsub:${topic}`, receiver as (e: Event) => void);
		}
	};
}

/** @deprecated We should do non-react async extensions */
export const ASYNC_EXTENSION = 'react-extension-async';

// HACK: we are actually breaking rules of hooks afterall.
let hasRendered = false;

export const AsyncExtensionsMaster: React.FC<React.PropsWithChildren> = function AsyncExtensions({ children }) {
	// we need to observe the rules around hooks.
	// this is so the react-extension-async can call react hooks safely
	// we need to not change the number of hooks being called
	// and this method MIGHT be called many times (as identity is above us and we might have a re-render due to getting a new token)

	// the solutions to these are to obtain the hook-extensions on first render and store that in a ref. this should maintain a static list from that point
	// I am spreading the array in the useRef call to ensure the array itself can't be modified accidentally or by subsequent registrations
	// to control subsequent render registrations, the hasRendered ref keeps track of this and is passed into the registration function

	const initialHooks = React.useRef([...getExtensions<(reg: boolean) => void>(ASYNC_EXTENSION)]);

	// it is unlikely current will be flipped early but I am in extra paranoia mode
	const thisHasRendered = hasRendered;
	hasRendered = true;
	for (const h of initialHooks.current) {
		h(thisHasRendered);
	}
	return <>{children}</>;
};

export const ExtensionContextProvider = ({ children }: IExtensionProviderProps) => {
	return <ExtensionContext.Provider value={extensionContextValue}>{children}</ExtensionContext.Provider>;
};

if (!!win['__DEV_EXTENSION_STORE']) {
	console.warn('There appears to be an existing store mounted on the page; this is unsupported.');
} else {
	// debugging
	win['__DEV_EXTENSION_STORE'] = extensionContextStore;
	win['__DEV_EXTENSION_API'] = api;
}

export const getExtensionRoutes = (): IExtensionContextStore['routeMatches'] => {
	return extensionContextStore.routeMatches.filter(route => route.route?.parentRoute && route);
};
